import { useEffect, useState } from 'react';
import Layout from '../../components/Layout';
import { useAuthStore } from '../../store/auth';

interface UserRole {
    id: string;
    code: string;
    name: string;
}

interface TenantUser {
    id: string;
    email: string;
    full_name: string;
    phone: string | null;
    status: 'active' | 'blocked';
    avatar_url: string | null;
    roles: UserRole[];
    last_login_at: string | null;
    created_at: string;
}

export default function Users() {
    const { user: currentUser } = useAuthStore();
    const isAllowedToManage = currentUser?.roles?.includes('tenant_admin') || currentUser?.roles?.includes('super_admin');

    const [users, setUsers] = useState<TenantUser[]>([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [statusFilter, setStatusFilter] = useState('all');

    // Modals state
    const [showModal, setShowModal] = useState(false);
    const [editingUser, setEditingUser] = useState<TenantUser | null>(null);

    // Form inputs
    const [email, setEmail] = useState('');
    const [fullName, setFullName] = useState('');
    const [phone, setPhone] = useState('');
    const [password, setPassword] = useState('');
    const [roleCode, setRoleCode] = useState('tenant_member');
    const [status, setStatus] = useState<'active' | 'blocked'>('active');

    const [busy, setBusy] = useState(false);
    const [toast, setToast] = useState<{ msg: string; type: 'success' | 'error' } | null>(null);

    const API = import.meta.env.VITE_API_URL || '/api';
    const H = {
        Authorization: `Bearer ${localStorage.getItem('auth_token')}`,
        'Content-Type': 'application/json',
        Accept: 'application/json',
    };

    const showToast = (msg: string, type: 'success' | 'error' = 'success') => {
        setToast({ msg, type });
        setTimeout(() => setToast(null), 4000);
    };

    const fetchUsers = async () => {
        setLoading(true);
        try {
            const res = await fetch(`${API}/admin/users`, { headers: H });
            const data = await res.json();
            if (res.ok) {
                setUsers(data.data ?? []);
            } else {
                showToast(data.message ?? 'Lỗi tải danh sách thành viên', 'error');
            }
        } catch {
            showToast('Lỗi kết nối máy chủ', 'error');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchUsers();
    }, []);

    const handleOpenAddModal = () => {
        setEditingUser(null);
        setEmail('');
        setFullName('');
        setPhone('');
        setPassword('');
        setRoleCode('tenant_member');
        setStatus('active');
        setShowModal(true);
    };

    const handleOpenEditModal = (user: TenantUser) => {
        setEditingUser(user);
        setEmail(user.email);
        setFullName(user.full_name);
        setPhone(user.phone ?? '');
        setPassword('');
        setRoleCode(user.roles[0]?.code ?? 'tenant_member');
        setStatus(user.status);
        setShowModal(true);
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setBusy(true);

        const payload: any = {
            full_name: fullName,
            phone: phone || null,
            role_code: roleCode,
            status,
        };

        if (!editingUser) {
            payload.email = email;
            payload.password = password;
        } else if (password) {
            payload.password = password;
        }

        try {
            const url = editingUser ? `${API}/admin/users/${editingUser.id}` : `${API}/admin/users`;
            const method = editingUser ? 'PUT' : 'POST';

            const res = await fetch(url, {
                method,
                headers: H,
                body: JSON.stringify(payload),
            });
            const data = await res.json();

            if (res.ok) {
                showToast(editingUser ? 'Cập nhật thành công!' : 'Mời thành viên thành công!');
                setShowModal(false);
                fetchUsers();
            } else {
                showToast(data.message ?? 'Lỗi xử lý yêu cầu', 'error');
            }
        } catch {
            showToast('Lỗi kết nối máy chủ', 'error');
        } finally {
            setBusy(false);
        }
    };

    const handleDelete = async (id: string) => {
        if (!confirm('Bạn có chắc chắn muốn xóa thành viên này khỏi hệ thống?')) return;

        try {
            const res = await fetch(`${API}/admin/users/${id}`, {
                method: 'DELETE',
                headers: H,
            });
            const data = await res.json();
            if (res.ok) {
                showToast('Đã xóa thành viên!');
                fetchUsers();
            } else {
                showToast(data.message ?? 'Không thể xóa thành viên', 'error');
            }
        } catch {
            showToast('Lỗi kết nối', 'error');
        }
    };

    const handleToggleStatus = async (user: TenantUser) => {
        const newStatus = user.status === 'active' ? 'blocked' : 'active';
        try {
            const res = await fetch(`${API}/admin/users/${user.id}`, {
                method: 'PUT',
                headers: H,
                body: JSON.stringify({
                    full_name: user.full_name,
                    phone: user.phone,
                    status: newStatus,
                    role_code: user.roles[0]?.code ?? 'tenant_member',
                }),
            });
            if (res.ok) {
                showToast(newStatus === 'active' ? 'Đã kích hoạt tài khoản!' : 'Đã khóa tài khoản!');
                fetchUsers();
            } else {
                showToast('Lỗi đổi trạng thái', 'error');
            }
        } catch {
            showToast('Lỗi kết nối', 'error');
        }
    };

    const filteredUsers = users.filter((u) => {
        const matchesSearch =
            u.full_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
            u.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
            (u.phone && u.phone.includes(searchTerm));

        const matchesStatus =
            statusFilter === 'all' ||
            (statusFilter === 'active' && u.status === 'active') ||
            (statusFilter === 'blocked' && u.status === 'blocked');

        return matchesSearch && matchesStatus;
    });

    return (
        <Layout>
            {/* Toast Notification */}
            {toast && (
                <div
                    className={`fixed top-6 right-6 z-50 flex items-center gap-2 rounded-xl px-5 py-3 text-sm font-medium shadow-xl transition-all ${
                        toast.type === 'success' ? 'bg-green-500 text-white' : 'bg-red-500 text-white'
                    }`}
                >
                    <span>{toast.type === 'success' ? '✓' : '✗'}</span>
                    <span>{toast.msg}</span>
                </div>
            )}

            <div className="mx-auto max-w-6xl px-4 py-8">
                {/* Header Section */}
                <div className="mb-8 flex flex-col justify-between gap-4 sm:flex-row sm:items-center">
                    <div>
                        <h1 className="text-3xl font-extrabold text-gray-900 tracking-tight">Thành viên team</h1>
                        <p className="mt-1 text-sm text-gray-500">
                            Quản lý các nhân sự tham gia quản lý chiến dịch và xem báo cáo.
                        </p>
                    </div>
                    {isAllowedToManage && (
                        <button
                            onClick={handleOpenAddModal}
                            className="inline-flex items-center justify-center gap-2 rounded-xl bg-blue-600 px-4 py-2.5 text-sm font-semibold text-white shadow-sm transition-all hover:bg-blue-700"
                        >
                            ➕ Mời thành viên
                        </button>
                    )}
                </div>

                {/* Filter and Search Section */}
                <div className="mb-6 flex flex-col gap-4 sm:flex-row sm:items-center justify-between">
                    <div className="relative flex-1 max-w-md">
                        <span className="absolute inset-y-0 left-0 flex items-center pl-3 text-gray-400">🔍</span>
                        <input
                            type="text"
                            placeholder="Tìm theo tên, email hoặc SĐT..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className="w-full rounded-xl border border-gray-200 bg-white py-2.5 pl-10 pr-4 text-sm outline-none transition-all focus:border-blue-500 focus:ring-2 focus:ring-blue-100"
                        />
                    </div>
                    <div className="flex items-center gap-2">
                        <span className="text-sm text-gray-400">Trạng thái:</span>
                        <select
                            value={statusFilter}
                            onChange={(e) => setStatusFilter(e.target.value)}
                            className="rounded-xl border border-gray-200 bg-white px-3 py-2 text-sm outline-none transition-all focus:border-blue-500"
                        >
                            <option value="all">Tất cả</option>
                            <option value="active">Đang hoạt động</option>
                            <option value="blocked">Đang khóa</option>
                        </select>
                    </div>
                </div>

                {/* Users Smart Table */}
                <div className="overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-sm">
                    {loading ? (
                        <div className="flex h-64 items-center justify-center">
                            <div className="h-8 w-8 animate-spin rounded-full border-4 border-blue-500 border-t-transparent" />
                        </div>
                    ) : filteredUsers.length === 0 ? (
                        <div className="flex h-64 flex-col items-center justify-center p-8 text-center">
                            <span className="text-4xl mb-2">👥</span>
                            <h3 className="text-lg font-bold text-gray-900">Không tìm thấy thành viên nào</h3>
                            <p className="mt-1 text-sm text-gray-400">
                                Hãy thử thay đổi bộ lọc tìm kiếm hoặc mời thành viên mới.
                            </p>
                        </div>
                    ) : (
                        <table className="w-full border-collapse text-left text-sm">
                            <thead className="bg-gray-50 border-b border-gray-200">
                                <tr>
                                    <th className="px-6 py-4 font-semibold text-gray-500 uppercase tracking-wider text-xs">Thành viên</th>
                                    <th className="px-6 py-4 font-semibold text-gray-500 uppercase tracking-wider text-xs">Vai trò</th>
                                    <th className="px-6 py-4 font-semibold text-gray-500 uppercase tracking-wider text-xs">Trạng thái</th>
                                    <th className="px-6 py-4 font-semibold text-gray-500 uppercase tracking-wider text-xs">Đăng nhập cuối</th>
                                    {isAllowedToManage && <th className="px-6 py-4 font-semibold text-gray-500 uppercase tracking-wider text-xs text-right">Hành động</th>}
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-100">
                                {filteredUsers.map((user) => {
                                    const role = user.roles[0];
                                    const isBlocked = user.status === 'blocked';

                                    return (
                                        <tr key={user.id} className="hover:bg-gray-50/50 transition-colors">
                                            <td className="px-6 py-4">
                                                <div className="flex items-center gap-3">
                                                    <div className="flex h-10 w-10 items-center justify-center rounded-full bg-blue-100 font-bold text-blue-600 text-sm">
                                                        {user.avatar_url ? (
                                                            <img src={user.avatar_url} alt="" className="h-full w-full rounded-full object-cover" />
                                                        ) : (
                                                            user.full_name.charAt(0).toUpperCase()
                                                        )}
                                                    </div>
                                                    <div>
                                                        <div className="font-semibold text-gray-900">{user.full_name}</div>
                                                        <div className="text-xs text-gray-400 mt-0.5">{user.email}</div>
                                                        {user.phone && <div className="text-[10px] text-gray-400">{user.phone}</div>}
                                                    </div>
                                                </div>
                                            </td>
                                            <td className="px-6 py-4">
                                                <span
                                                    className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-semibold ${
                                                        role?.code === 'super_admin'
                                                            ? 'bg-red-100 text-red-700'
                                                            : role?.code === 'tenant_admin'
                                                            ? 'bg-purple-100 text-purple-700'
                                                            : 'bg-blue-100 text-blue-700'
                                                    }`}
                                                >
                                                    {role?.name ?? 'Thành viên'}
                                                </span>
                                            </td>
                                            <td className="px-6 py-4">
                                                <span
                                                    className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5 text-xs font-semibold ${
                                                        isBlocked
                                                            ? 'bg-red-100 text-red-700'
                                                            : 'bg-green-100 text-green-700'
                                                    }`}
                                                >
                                                    <span className={`h-1.5 w-1.5 rounded-full ${isBlocked ? 'bg-red-500' : 'bg-green-500'}`} />
                                                    {isBlocked ? 'Bị khóa' : 'Đang hoạt động'}
                                                </span>
                                            </td>
                                            <td className="px-6 py-4 text-gray-500">
                                                {user.last_login_at
                                                    ? new Date(user.last_login_at).toLocaleString('vi-VN')
                                                    : 'Chưa đăng nhập'}
                                            </td>
                                            {isAllowedToManage && (
                                                <td className="px-6 py-4 text-right">
                                                    <div className="flex justify-end gap-2">
                                                        <button
                                                            onClick={() => handleToggleStatus(user)}
                                                            title={isBlocked ? 'Kích hoạt tài khoản' : 'Khóa tài khoản'}
                                                            className={`rounded-lg p-1.5 text-xs font-semibold transition-all ${
                                                                isBlocked
                                                                    ? 'bg-green-50 text-green-600 hover:bg-green-100'
                                                                    : 'bg-amber-50 text-amber-600 hover:bg-amber-100'
                                                            }`}
                                                        >
                                                            {isBlocked ? '🔓 Mở' : '🔒 Khóa'}
                                                        </button>
                                                        <button
                                                            onClick={() => handleOpenEditModal(user)}
                                                            className="rounded-lg bg-blue-50 p-1.5 text-xs font-semibold text-blue-600 transition-all hover:bg-blue-100"
                                                        >
                                                            ✏️ Sửa
                                                        </button>
                                                        <button
                                                            onClick={() => handleDelete(user.id)}
                                                            className="rounded-lg bg-red-50 p-1.5 text-xs font-semibold text-red-600 transition-all hover:bg-red-100"
                                                        >
                                                            🗑️ Xóa
                                                        </button>
                                                    </div>
                                                </td>
                                            )}
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    )}
                </div>
            </div>

            {/* Invite / Edit Modal */}
            {showModal && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4 backdrop-blur-sm">
                    <div className="w-full max-w-md overflow-hidden rounded-2xl bg-white shadow-2xl transition-all">
                        <div className="bg-gray-50 border-b border-gray-200 px-6 py-4 flex items-center justify-between">
                            <h3 className="text-lg font-bold text-gray-900">
                                {editingUser ? '✏️ Chỉnh sửa thành viên' : '➕ Mời thành viên mới'}
                            </h3>
                            <button
                                onClick={() => setShowModal(false)}
                                className="text-gray-400 hover:text-gray-600 text-xl font-bold"
                            >
                                &times;
                            </button>
                        </div>
                        <form onSubmit={handleSubmit} className="p-6 space-y-4">
                            <div>
                                <label className="block text-xs font-semibold text-gray-500 uppercase mb-1">Email đăng nhập</label>
                                <input
                                    type="email"
                                    required
                                    disabled={!!editingUser}
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    className="w-full rounded-xl border border-gray-200 px-4 py-2.5 text-sm outline-none transition-all focus:border-blue-500 focus:ring-2 focus:ring-blue-100 disabled:bg-gray-50 disabled:text-gray-400"
                                />
                            </div>

                            <div>
                                <label className="block text-xs font-semibold text-gray-500 uppercase mb-1">Họ và tên</label>
                                <input
                                    type="text"
                                    required
                                    value={fullName}
                                    onChange={(e) => setFullName(e.target.value)}
                                    className="w-full rounded-xl border border-gray-200 px-4 py-2.5 text-sm outline-none transition-all focus:border-blue-500 focus:ring-2 focus:ring-blue-100"
                                />
                            </div>

                            <div>
                                <label className="block text-xs font-semibold text-gray-500 uppercase mb-1">Số điện thoại</label>
                                <input
                                    type="text"
                                    value={phone}
                                    onChange={(e) => setPhone(e.target.value)}
                                    className="w-full rounded-xl border border-gray-200 px-4 py-2.5 text-sm outline-none transition-all focus:border-blue-500 focus:ring-2 focus:ring-blue-100"
                                />
                            </div>

                            <div>
                                <label className="block text-xs font-semibold text-gray-500 uppercase mb-1">
                                    Mật khẩu {editingUser && <span className="text-[10px] text-gray-400 lowercase">(bỏ trống nếu giữ nguyên)</span>}
                                </label>
                                <input
                                    type="password"
                                    required={!editingUser}
                                    value={password}
                                    onChange={(e) => setPassword(e.target.value)}
                                    className="w-full rounded-xl border border-gray-200 px-4 py-2.5 text-sm outline-none transition-all focus:border-blue-500 focus:ring-2 focus:ring-blue-100"
                                />
                            </div>

                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-xs font-semibold text-gray-500 uppercase mb-1">Vai trò</label>
                                    <select
                                        value={roleCode}
                                        onChange={(e) => setRoleCode(e.target.value)}
                                        className="w-full rounded-xl border border-gray-200 px-3 py-2.5 text-sm outline-none focus:border-blue-500"
                                    >
                                        <option value="tenant_member">Thành viên</option>
                                        <option value="tenant_admin">Admin</option>
                                        {currentUser?.roles?.includes('super_admin') && (
                                            <option value="super_admin">Super Admin</option>
                                        )}
                                    </select>
                                </div>
                                <div>
                                    <label className="block text-xs font-semibold text-gray-500 uppercase mb-1">Trạng thái</label>
                                    <select
                                        value={status}
                                        onChange={(e) => setStatus(e.target.value as any)}
                                        className="w-full rounded-xl border border-gray-200 px-3 py-2.5 text-sm outline-none focus:border-blue-500"
                                    >
                                        <option value="active">Hoạt động</option>
                                        <option value="blocked">Khóa</option>
                                    </select>
                                </div>
                            </div>

                            <div className="pt-4 border-t border-gray-100 flex justify-end gap-2">
                                <button
                                    type="button"
                                    onClick={() => setShowModal(false)}
                                    className="rounded-xl border border-gray-200 px-4 py-2.5 text-sm font-semibold text-gray-600 transition-all hover:bg-gray-50"
                                >
                                    Hủy
                                </button>
                                <button
                                    type="submit"
                                    disabled={busy}
                                    className="inline-flex items-center justify-center rounded-xl bg-blue-600 px-4 py-2.5 text-sm font-semibold text-white shadow-sm transition-all hover:bg-blue-700 disabled:opacity-60"
                                >
                                    {busy ? (
                                        <span className="flex items-center gap-2">
                                            <svg className="w-4 h-4 animate-spin text-white" viewBox="0 0 24 24" fill="none">
                                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
                                            </svg>
                                            Đang lưu...
                                        </span>
                                    ) : (
                                        'Lưu thông tin'
                                    )}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </Layout>
    );
}
